import {TreeBaseItem, TreeNodeProps, TreeViewClasses} from './types';
import TreeNodeChildren from './TreeNodeChildren';
import {
    CircularProgress,
    Collapse,
    IconButton,
    ListItemButton,
} from '@mui/material';
import classNames from 'classnames';
import React, {useState} from 'react';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';

export default function BaseTreeNode<D extends TreeBaseItem>(
    props: TreeNodeProps<D>
) {
    const {
        node,
        disabled,
        branchDisabled,
        disabledBranches,
        disabledNodes,
        renderNodeLabel,
        level,
        onToggleSelect,
        onToggleExpand,
        selectedNodes,
        expandedNodes,
        onNodeAdd,
        onNodeRemove,
        onNodeUpdate,
        editNodeComponent,
        onNodeStartEdit,
        onNodeCancelEdit,
        isSelectable,
    } = props;

    const selected = selectedNodes.includes(node.id);
    const expanded = expandedNodes.includes(node.id);

    const resolvedBranchDisabled =
        branchDisabled || disabledBranches?.includes(node.id);

    const resolvedDisabled =
        resolvedBranchDisabled || disabled || disabledNodes?.includes(node.id);
    const resolvedSelectable = isSelectable ? isSelectable(node) : true;

    const [expanding, setExpanding] = useState(false);

    return (
        <>
            <ListItemButton
                className={classNames({
                    [TreeViewClasses.Node]: true,
                    [TreeViewClasses.NodeSelected]: selected,
                    [TreeViewClasses.NodeExpanded]: expanded,
                    [TreeViewClasses.NodeDisabled]:
                        resolvedDisabled || !resolvedSelectable,
                })}
                disabled={resolvedDisabled}
                selected={selected}
                onClick={() => {
                    if (resolvedSelectable) {
                        onToggleSelect(node, !selected);
                    } else {
                        onToggleExpand(node, true);
                    }
                }}
            >
                <div
                    className={TreeViewClasses.NodeArrow}
                    onClick={async e => {
                        e.stopPropagation();

                        setExpanding(true);
                        try {
                            await onToggleExpand(node, !expanded);
                        } finally {
                            setExpanding(false);
                        }
                    }}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {node.hasChildren ? (
                        expanding ? (
                            <CircularProgress size={15} />
                        ) : (
                            <ExpandMoreIcon />
                        )
                    ) : null}
                </div>
                <div className={TreeViewClasses.NodeLabel}>
                    {editNodeComponent && node.editing
                        ? React.createElement(editNodeComponent, {
                              ...props,
                              onFinishEdit: (data: D) => {
                                  onNodeUpdate?.(node, {
                                      ...node,
                                      data: {
                                          ...(node.data ?? {}),
                                          ...data,
                                      },
                                  });
                              },
                              onCancelEdit: () => {
                                  if (onNodeRemove && !node.editedOnce) {
                                      onNodeRemove?.(node);
                                  } else {
                                      onNodeCancelEdit?.(node);
                                  }
                              },
                          })
                        : renderNodeLabel({
                              level,
                              node,
                          })}
                </div>
                <div>
                    {editNodeComponent &&
                    onNodeStartEdit &&
                    !node.editing &&
                    node.canEdit ? (
                        <IconButton
                            onClick={() => onNodeStartEdit(node)}
                            onMouseDown={e => e.stopPropagation()}
                        >
                            <EditIcon />
                        </IconButton>
                    ) : null}
                    {onNodeRemove && !node.editing && node.canDelete ? (
                        <IconButton
                            onClick={() => {
                                onToggleSelect(node, false);
                                onNodeRemove(node);
                            }}
                            onMouseDown={e => e.stopPropagation()}
                        >
                            <DeleteIcon />
                        </IconButton>
                    ) : null}
                    {onNodeAdd && !node.editing && node.canAddChildren ? (
                        <IconButton
                            onClick={e => {
                                e.stopPropagation();
                                onNodeAdd(node, {});
                                onToggleExpand(node, true);
                            }}
                            onMouseDown={e => e.stopPropagation()}
                        >
                            <CreateNewFolderIcon />
                        </IconButton>
                    ) : null}
                </div>
            </ListItemButton>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box
                    className={TreeViewClasses.NodeChildren}
                    sx={{
                        pl: 1 + level / 4,
                    }}
                >
                    <TreeNodeChildren
                        {...props}
                        branchDisabled={resolvedBranchDisabled}
                        level={level + 1}
                    />
                </Box>
            </Collapse>
        </>
    );
}
